import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MonoKicker,
  GoldRule,
  PosterHeadline,
  SerifStory,
  GoldClause,
  GoldCTA,
  CtaMicroline,
  IdentityLegend,
  Section,
  SectionMarker,
  Reveal,
  Device,
  APP_GLOBE_ARCS,
  APP_ENCOUNTER_MAP,
  APP_TIMELINE,
  APP_GLOBE_NASA,
} from '../index';

/**
 * §02 THE EVIDENCE — the in-app spectacle.
 *
 * The one horizontal moment on a page that is otherwise a vertical column.
 * That contrast is the point: the scroll rhythm needs a section that moves
 * sideways.
 *
 * Zero numbers appear in this section's own copy, and none appear inside its
 * screenshots either — Exhibit 02 ships the crop with the stat pair, street
 * address and coordinates cut away.
 *
 * The identity legend above the rail is mandatory, not decorative: Exhibit 02
 * is the only place on the site where the gold/coral encoding is visible, and
 * the legend is what makes it readable without hue. Glyph and word are both
 * in the DOM, so it survives grayscale.
 *
 * "Your top three encounters, free" is the free-tier fact — code-verified,
 * lead-ratified. It is NOT the same claim as friend mode's "first connection
 * free". Never merge them; conflating the two creates a third claim that is
 * true of neither.
 */

const EXHIBITS = [
  {
    asset: APP_GLOBE_ARCS,
    plate: 'Exhibit 01 · The globe',
    caption: "Every crossing you've ever had, drawn as an arc between two points in time.",
  },
  {
    asset: APP_ENCOUNTER_MAP,
    plate: 'Exhibit 02 · One encounter',
    caption:
      'Open any crossing to see exactly where you both were — and exactly how narrowly you missed each other.',
  },
  {
    asset: APP_TIMELINE,
    plate: 'Exhibit 03 · The timeline',
    caption: 'Every near-miss in order — when, where, and how near.',
  },
  {
    asset: APP_GLOBE_NASA,
    plate: 'Exhibit 04 · The whole earth',
    caption: 'The earth is dark. Your story is the only light on it.',
  },
];

function Exhibit({ exhibit, index, stagger }) {
  const { asset, plate, caption } = exhibit;
  return (
    <li
      className={`flex w-[240px] flex-none snap-start flex-col md:w-[260px] lg:w-auto lg:flex-1 lg:basis-0 ${
        // Stagger devices 2 and 4 down at desktop — a flat four-up row of
        // phones is exactly the generic app-marketing layout being avoided.
        index % 2 === 1 ? 'lg:mt-14' : ''
      }`}
    >
      {/* h-full has to run unbroken from the li through Reveal to the inner
          column, or the device never bottom-aligns. Exhibit 02 is deliberately
          much shorter than the other three; it is bottom-aligned to them and
          never stretched or letterboxed to match. */}
      <Reveal delay={stagger} className="flex h-full flex-col">
        <div className="flex flex-1 items-end">
          <Device
            src={asset.src}
            alt={asset.alt}
            width={asset.width}
            height={asset.height}
            className="w-full"
          />
        </div>
        {/* Plate labels reserve two lines so the hairlines land on one
            baseline across all four cards — misaligned hairlines destroy the
            case-file effect. */}
        <p className="cp-mono mt-5 text-[length:var(--fs-mono)] text-gold-400 lg:min-h-[2.6em]">
          {plate}
        </p>
        <span aria-hidden="true" className="mt-2 block h-px w-full bg-[color:var(--cp-hairline)]" />
        <p className="mt-3 text-[length:var(--fs-body)] leading-[1.6] text-ink-2 lg:min-h-[5.5rem]">
          {caption}
        </p>
      </Reveal>
    </li>
  );
}

export function Showcase() {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);

  // IntersectionObserver on the cards rather than a scroll handler — no
  // per-frame work while the rail is being flicked.
  const observe = useCallback(() => {
    const rail = railRef.current;
    if (!rail || typeof IntersectionObserver === 'undefined') return undefined;
    const cards = [...rail.querySelectorAll('li')];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(cards.indexOf(entry.target));
        });
      },
      { root: rail, threshold: 0.6 }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  useEffect(() => observe(), [observe]);

  return (
    <Section id="showcase" labelledBy="showcase-h" tone="canvas">
      <SectionMarker className="mb-10">§ 02 · The Evidence</SectionMarker>

      <div className="grid gap-8 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-6">
          <Reveal>
            <GoldRule />
            <MonoKicker className="mt-2">INSIDE THE APP</MonoKicker>
            <PosterHeadline
              as="h2"
              id="showcase-h"
              size="section"
              className="mt-3.5"
              lines={[{ text: 'Every crossing' }, { text: 'on one map', outline: true }]}
            />
          </Reveal>
          <Reveal delay={120}>
            <SerifStory className="mt-[18px] max-w-[46ch]">
              The where and the when, drawn from the photos already on your phone —{' '}
              <GoldClause>and nothing else.</GoldClause>
            </SerifStory>
          </Reveal>
        </div>

        {/* Identity legend — brand law and the colourblind gate. */}
        <div className="lg:col-span-5 lg:col-start-8 lg:pt-2">
          <Reveal delay={200}>
            <IdentityLegend includeCrossing />
          </Reveal>
        </div>
      </div>

      {/* ---- the exhibit rail ---- */}
      <div className="mt-14">
        {/* role/tabIndex/aria-label live on a WRAPPER, never on the <ul>.
            role="region" is not a permitted role for a list, and applying it
            replaces the implicit list role — which orphans every <li> from
            its parent and fires the list/listitem audits. The scroll
            container and the list are two different things, so they are now
            two different elements. */}
        <div
          ref={railRef}
          data-testid="showcase-rail"
          tabIndex={0}
          role="region"
          aria-label="App screenshots"
          className="showcase-rail -mx-5 overflow-x-auto px-5 pb-2 md:-mx-10 md:px-10 lg:mx-0 lg:overflow-x-visible lg:px-0 xl:mx-0 xl:px-0"
        >
          <ul className="flex snap-x snap-mandatory items-stretch gap-5 lg:gap-6">
            {EXHIBITS.map((exhibit, i) => (
              <Exhibit key={exhibit.plate} exhibit={exhibit} index={i} stagger={80 * i} />
            ))}
          </ul>
        </div>

        {/* Progress. Dots alone would be a colour-and-shape-only affordance,
            so the mono counter carries the information and the dots are
            decorative. */}
        <div className="mt-6 flex items-center gap-3 lg:hidden">
          <span aria-hidden="true" className="flex gap-2">
            {EXHIBITS.map((e, i) => (
              <span
                key={e.plate}
                className={`block h-1.5 w-1.5 rounded-full transition-colors duration-300 ${
                  i === active ? 'bg-gold-400' : 'bg-[color:var(--cp-stroke-muted)]'
                }`}
              />
            ))}
          </span>
          <span className="cp-mono text-[length:var(--fs-mono)]">
            {String(active + 1).padStart(2, '0')} / {String(EXHIBITS.length).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* ---- CTA closing the band ---- */}
      <Reveal delay={200} className="mt-16 max-w-[560px]">
        <div className="flex flex-col items-start gap-1 pb-[10px] min-[421px]:flex-row min-[421px]:items-baseline">
          <span className="cp-mono text-[length:var(--fs-mono)] whitespace-nowrap">
            What you see free
          </span>
          <span
            aria-hidden="true"
            className="mx-3 hidden min-w-[24px] flex-1 -translate-y-[0.28em] border-b border-dotted border-[color:var(--cp-stroke-muted)] min-[421px]:block"
          />
          <span className="cp-mono text-[length:var(--fs-mono-value)] text-gold-400">
            Your top three encounters
          </span>
        </div>
        <span aria-hidden="true" className="block h-px w-full bg-[color:var(--cp-hairline)]" />

        <div className="mt-8 flex flex-col items-start gap-4">
          <GoldCTA data-testid="showcase-download" fullWidth className="min-[560px]:w-auto">
            Discover Your Crossed Paths
          </GoldCTA>
          <CtaMicroline className="w-full" />
        </div>
      </Reveal>
    </Section>
  );
}

export default Showcase;
